import { ISO_3166_ALPHA2_CODES } from '../data/countries/countries.js';

/**
 * Per-country address rules.
 *
 * Two entries: the United States, whose addresses are verified against Smarty's
 * US Autocomplete Pro and US Street APIs, and a generic entry covering every
 * other country, whose addresses are accepted as typed with no verification.
 *
 * The registry holds the facts that `addressSchema` needs to validate an address
 * and that the address form needs to label its fields. It deliberately does not
 * hold the administrative-area option list: which 51 entries populate the US
 * dropdown is form presentation, not a property of the country, and keeping the
 * list out means this module stays free of heavyweight imports. `hasFixedAdminAreaOptions`
 * tells a form whether to render a dropdown at all; the form supplies the options.
 */
/** ISO 3166-1 alpha-2 code for the United States, the only country with a named entry. */
const US_COUNTRY_CODE = 'US';
/**
 * Spellings of the United States that appear in stored addresses.
 *
 * `USA` is in live use, not hypothetical: the Lightspeed inventory adapter
 * writes it and several fixtures carry it. Treating it as an unnamed country
 * would read a genuinely US address as international, which would gate it out
 * of titling and the tax engine and stop requiring its state and ZIP. Normalize
 * instead of assuming every writer settled on the ISO code.
 */
const US_COUNTRY_ALIASES = new Set([
    US_COUNTRY_CODE,
    'USA',
    'U.S.',
    'U.S.A.',
    'UNITED STATES',
    'UNITED STATES OF AMERICA',
]);
const US_ADDRESS_RULES = {
    code: US_COUNTRY_CODE,
    isVerified: true,
    adminAreaLabel: 'State',
    isAdminAreaRequired: true,
    hasFixedAdminAreaOptions: true,
    postalCodeLabel: 'Zip code',
    isPostalCodeRequired: true,
    hasPostalCodeExtension: true,
};
/**
 * Rules for every country the registry does not name. Neither an administrative
 * area nor a postal code is required, because plenty of countries have no
 * equivalent of either.
 */
const DEFAULT_ADDRESS_COUNTRY_RULES = {
    code: null,
    isVerified: false,
    adminAreaLabel: 'State / Province / Region',
    isAdminAreaRequired: false,
    hasFixedAdminAreaOptions: false,
    postalCodeLabel: 'Postal code',
    isPostalCodeRequired: false,
    hasPostalCodeExtension: false,
};
/**
 * Countries with rules of their own, keyed by ISO 3166-1 alpha-2.
 *
 * Adding a country here is a data change, not a code change. A Canadian entry
 * would carry `adminAreaLabel: 'Province'`, a required postal code and
 * `hasFixedAdminAreaOptions: true` without any verification work.
 */
const SUPPORTED_COUNTRIES = {
    [US_COUNTRY_CODE]: US_ADDRESS_RULES,
};
/**
 * Resolves the rules governing an address in `country`.
 *
 * An absent or blank country resolves to the US. Every address stored before
 * this registry existed is a US address with no `country` set, so treating
 * absence as US is what keeps that data validating unchanged.
 */
function resolveAddressCountryRules(country) {
    const normalized = country?.trim().toUpperCase();
    if (!normalized || US_COUNTRY_ALIASES.has(normalized)) {
        return US_ADDRESS_RULES;
    }
    return SUPPORTED_COUNTRIES[normalized] ?? DEFAULT_ADDRESS_COUNTRY_RULES;
}
/** Whether `country` is the United States, treating an absent country as US. */
function isUsCountryCode(country) {
    return resolveAddressCountryRules(country).code === US_COUNTRY_CODE;
}
const KNOWN_COUNTRY_CODES = new Set(ISO_3166_ALPHA2_CODES);
/**
 * Whether `country` is a country this system recognizes.
 *
 * An absent country is recognized, because it means the US. Anything else must
 * be a real ISO 3166-1 alpha-2 code or one of the US aliases. This is what stops
 * an unrecognized value silently buying the permissive generic rules: without
 * the check, a caller sending a typo for a US address skips the state and postal
 * code requirements entirely, and the endpoints that accept an address are not
 * all behind a session.
 */
function isKnownCountryCode(country) {
    const normalized = country?.trim().toUpperCase();
    if (!normalized) {
        return true;
    }
    return US_COUNTRY_ALIASES.has(normalized) || KNOWN_COUNTRY_CODES.has(normalized);
}

export { DEFAULT_ADDRESS_COUNTRY_RULES, SUPPORTED_COUNTRIES, US_COUNTRY_CODE, isKnownCountryCode, isUsCountryCode, resolveAddressCountryRules };
//# sourceMappingURL=Country.js.map
